/**
 * P3-31: Ziggurat 面板。
 */
import { useMemo } from "react";
import { getGame } from "../../bridge/GameBridge";
import { useGameTick } from "../../bridge/useGameTick";
import type { GamePage } from "../../bridge/GameBridge";
import { Panel } from "../../components/Panel";
import { t } from "../../bridge/i18n";
import { MultiLinkButton } from "./MultiLinkButton";
import { RefineTearsButton } from "./RefineTearsButton";
import { ZigguratUpgradeButton } from "./ZigguratUpgradeButton";
import {
  useUnicornSacrificeButtonModel,
  useTransformButtonModelWithCtor,
} from "./useReligionModel";

export function ZigguratPanel() {
  const game = useGameTick((g: GamePage | null) => g);

  const alicornExtra = useMemo(
    () => ({
      updateVisible: (model: { visible: boolean }) => {
        model.visible = true;
      },
      gainMultiplier: () => 1 + ((getGame()?.getEffect("tcRefineRatio") as number) ?? 0),
      gainedResource: "timeCrystal",
      applyAtGain: () => {
        getGame()?.upgrade({ zigguratUpgrades: ["skyPalace", "unicornUtopia", "sunspire"] });
      },
      logTextID: "religion.sacrificeAlicornsBtn.sacrifice.msg",
      logfilterID: "alicornSacrifice",
    }),
    [],
  );

  const refineTCExtra = useMemo(
    () => ({
      updateVisible: (model: { visible: boolean }) => {
        model.visible = true;
      },
      gainMultiplier: () => {
        const g = getGame();
        const bp = g?.religion?.getZU?.("blackPyramid");
        return 1 + ((g?.getEffect("relicRefineRatio") as number) ?? 0) * (bp?.getEffectiveValue?.(g) ?? 0);
      },
      gainedResource: "relic",
      logTextID: "religion.refineTCsBtn.refine.msg",
      logfilterID: "tcRefine",
    }),
    [],
  );

  const unicornSac = useUnicornSacrificeButtonModel(
    t("religion.sacrificeBtn.label"),
    t("religion.sacrificeBtn.desc"),
    [{ name: "unicorns", val: 2500 }],
  );

  const alicornSac = useTransformButtonModelWithCtor(
    t("religion.sacrificeAlicornsBtn.label"),
    t("religion.sacrificeAlicornsBtn.desc"),
    [{ name: "alicorn", val: 25 }],
    alicornExtra,
  );

  const refineTC = useTransformButtonModelWithCtor(
    t("religion.refineTCsBtn.label"),
    t("religion.refineTCsBtn.desc"),
    [{ name: "timeCrystal", val: 25 }],
    refineTCExtra,
  );

  if (!game) {
    return null;
  }

  const zigguratOn = (game.bld.get("ziggurat") as { on?: number }).on ?? 0;
  if (zigguratOn <= 0) {
    return null;
  }

  const zigguratUpgrades = (game.religion as { zigguratUpgrades?: { name: string }[] }).zigguratUpgrades ?? [];

  return (
    <Panel title={t("religion.panel.ziggurat.label")}>
      <MultiLinkButton model={unicornSac} />
      <MultiLinkButton model={alicornSac} />
      <RefineTearsButton />
      <MultiLinkButton model={refineTC} />
      {zigguratUpgrades.map((upgr) => (
        <ZigguratUpgradeButton key={upgr.name} upgradeName={upgr.name} />
      ))}
    </Panel>
  );
}
